import React, {Dispatch, useEffect, useState} from 'react';
import {
    Switch,
    Route,
    RouteComponentProps,
    withRouter,
    useLocation
} from 'react-router-dom';
import {connect} from 'react-redux';

import * as Routes from './routes';

import {Navbar}                      from './components/Navbar';
import {PrivateRoute}                from './components/PrivateRoute';
import {ErrorBoundary}               from './components/ErrorBoundary';
import {Feedback}                    from './components/Feedback';
import {Alert}                       from './components/Alert';
import {DevelopmentEnvironmentAlert} from './components/DevelopmentEnvironmentAlert';
import {Post}                        from './components/Post';
import PageStatus                    from './components/PageStatus';
import {getUserIsAuthenticated}  from './_selectors/user';
import {getStatusMessage}        from './_selectors/statusMessage';
import {clearStatusMessage}      from './_actions/statusMessage';
import {logout}                  from './_actions/user';
import {Flow, FlowFetchResponse} from './_services/Flow';

import {Post as PostType, StatusMessage} from './types';
import {setConnectedState} from './_actions/rtms';
import {NotificationManager} from './components/NotificationManager';

type AppProps = RouteComponentProps & {
    isAuthenticated: boolean;
    dispatchUserLogout: () => void;
    statusMessage: StatusMessage;
    dispatchClearStatusMessage: () => void;
};

const App: React.FunctionComponent<AppProps> = ({
                                                    isAuthenticated,
                                                    statusMessage,
                                                    dispatchClearStatusMessage,
                                                }): JSX.Element => {

    const location = useLocation<Location>();
    // let events: object[] = [];

    const [posts, setPosts] = useState<PostType[]>([]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    useEffect(() => {
        Flow.getInstance().getPosts('site')
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setPosts(response?.json?.posts);
                } else {
                    throw Error(response?.json?.message || 'Error fetching posts');
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }, [isAuthenticated]);

    const scriptVersion = document.getElementById('root')?.getAttribute('script-version') || '';
    const formOnlyInterface = (document.getElementById('root')?.getAttribute('form-only-interface') || '') === 'true';

    // TODO - this needs to be made more generic
    // not sure where logic should live, so put it all here for now
    const formOnlyInterfaceFormName = (document.getElementById('root')?.getAttribute('form-name') || '');
    let formOnlyInterfaceBrandName = '';
    if (formOnlyInterface && formOnlyInterfaceFormName === '402') {
        formOnlyInterfaceBrandName = 'empower'
    }
    if (formOnlyInterface && formOnlyInterfaceFormName === '493') {
        formOnlyInterfaceBrandName = 'Power Connections'
    }

    return (
        <div>
            <div id="main-container">
                {isAuthenticated && !formOnlyInterface && <NotificationManager />}
                {isAuthenticated && <Navbar brandingOnly={formOnlyInterface} brandingDisplayName={formOnlyInterfaceBrandName} />}
                {isAuthenticated && !formOnlyInterface && <Feedback/>}
                {
                    // @ts-ignore - PRODUCTION defined in webpack as build constant
                    !PRODUCTION && <DevelopmentEnvironmentAlert />
                }
                {isAuthenticated && !formOnlyInterface && posts?.length > 0 &&
                <div className="container mb-2" id="posts-container">
                  <div className="row">
                      {posts.map((post: PostType) => <Post key={post.id} post={post} />)}
                  </div>
                </div>
                }
                <div className="container shadow-sm rounded form-box-container pt-2"
                     id="main-inner-container"
                >

                    {statusMessage &&
                    <div className="row">
                      <div className="col-8 offset-2">
                        <Alert
                          type={statusMessage.type}
                          message={statusMessage.message}
                          icon={statusMessage.icon}
                          isDismissable={true}
                          dismiss={dispatchClearStatusMessage}
                        />
                      </div>
                    </div>
                    }

                    <ErrorBoundary>
                        <Switch>
                            <Route path="/login">
                                <Routes.Login />
                            </Route>
                            <Route path="/register">
                                <Routes.Register />
                            </Route>
                            <PrivateRoute path="/elonboarding">
                                <Routes.OnBoarding />
                            </PrivateRoute>
                            <PrivateRoute path="/paonboarding">
                                <Routes.PaOnboarding />
                            </PrivateRoute>
                            <PrivateRoute path="/paonboarding-manager-review">
                                <Routes.PaOnboardingManagerReview />
                            </PrivateRoute>
                            <PrivateRoute path="/paonboarding-manager-list">
                                <Routes.PaOnboardingManagerList />
                            </PrivateRoute>

                            <PrivateRoute path="/user/delegations">
                                <Routes.Delegations />
                            </PrivateRoute>

                            <PrivateRoute path="/admin/assignee-maintenance/:workflowId">
                                <Routes.AssigneeMaintenance />
                            </PrivateRoute>
                            <PrivateRoute path="/admin/role-maintenance/:workflowId">
                                <Routes.RoleMaintenance />
                            </PrivateRoute>
                            <PrivateRoute path="/admin/wfo-maintenance/:workflowId/:roleName?">
                                <Routes.RoleMaintenance pageTitle="Workflow Owners" />
                            </PrivateRoute>
                            <PrivateRoute path="/admin/ext-maintenance/:workflowId/:roleName?">
                                <Routes.RoleMaintenance pageTitle="External Users"
                                                        hasSponsorField={true}
                                />
                            </PrivateRoute>
                            <PrivateRoute path="/admin/dropdown-maintenance/:workflowId">
                                <Routes.DropdownMaintenance />
                            </PrivateRoute>
                            <PrivateRoute path="/admin/table-maintenance/:workflowId">
                                <Routes.TableMaintenance />
                            </PrivateRoute>

                            <PrivateRoute path="/entry/:entryId">
                                <Routes.Entry />
                            </PrivateRoute>
                            <PrivateRoute path="/form/entry/:entryId">
                                <Routes.Entry />
                            </PrivateRoute>
                            <PrivateRoute path="/workflow/:workflowId">
                                <Routes.WorkflowHome />
                            </PrivateRoute>
                            <PrivateRoute path="/form/:formId">
                                <Routes.NewEntry />
                            </PrivateRoute>
                            <PrivateRoute path="/new/:formId">
                                <Routes.NewEntry />
                            </PrivateRoute>
                            <PrivateRoute path="/list/:workflowId/:parentWorkflowId?">
                                <Routes.List />
                            </PrivateRoute>
                            <PrivateRoute path="/dataform/:workflowId/:dataformId">
                                <Routes.DataForm />
                            </PrivateRoute>
                            <PrivateRoute path="/dashboard/:workflowId">
                                <Routes.Dashboard />
                            </PrivateRoute>
                            <PrivateRoute path="/support">
                                <Routes.Support />
                            </PrivateRoute>
                            <PrivateRoute path="/search">
                                <Routes.Search />
                            </PrivateRoute>
                            <PrivateRoute path="/my-requests">
                                <Routes.MyRequests />
                            </PrivateRoute>
                            <PrivateRoute path="/inbox/:workflowId?">
                                <Routes.Inbox />
                            </PrivateRoute>
                            <PrivateRoute path="/page/:slug">
                                <Routes.Page />
                            </PrivateRoute>
                            <PrivateRoute path="/:filterType?/:filter?">
                                <Routes.WorkflowList />
                            </PrivateRoute>
                        </Switch>
                    </ErrorBoundary>
                </div>
            </div>
            <footer className="container d-block d-sm-flex justify-content-between">
                <p className="text-muted">Flow v1.0.0 ({scriptVersion}) - © 2024 ABB, Inc.</p>
                <PageStatus />
            </footer>
        </div>
    );
};

const AppWithRouter = withRouter(App);

const mapStateToProps = (state: any) => ({
    isAuthenticated: getUserIsAuthenticated(state),
    statusMessage: getStatusMessage(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        dispatchUserLogout: () => {
            dispatch(logout());
        },
        dispatchClearStatusMessage: () => {
            dispatch(clearStatusMessage());
        },
        dispatchRtmsConnected: () => {
            dispatch(setConnectedState('Connected'));
        },
        dispatchRtmsReConnecting: () => {
            dispatch(setConnectedState('ReConnecting'));
        },
        dispatchRtmsDisconnected: () => {
            dispatch(setConnectedState('Disconnected'));
        }
    };
};

// @ts-ignore
const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(AppWithRouter);
export {ConnectedApp as App};
